import React, { useState, FC, Suspense } from 'react';
import { Text, Box, Icon, theme, Spinner } from '@bechtel/gds';
import { Trans } from '@lingui/macro';
import { SuspenseLoader, ErrorBoundary, useAppUser } from '@bechtel/client-common';

import {
  CrewMember,
  RodRequestCaddyStatusCode,
  RodRequestStatusCode,
  RodRequest,
  CloseRodRequestByStencilIdError,
  CloseRodRequestByStencilIdPayload,
  CloseRodRequestByStencilIdInput,
  RodRequestStencil,
  RodRequestStatusCodeByStencil,
} from '@bechtel/types';
import { CLOSE_ROD_REQUEST_BY_STENCIL_ID } from '@bechtel/client-gql';
import { ApolloError, useMutation } from '@apollo/client';
import { FooterButtonStyled, ProgressWeldFooterStyled, RequestStyled } from './styled';
import { filter, findIndex, get } from 'lodash';
import { ClosedRodRequestByStencilIdError } from '../error';

interface CloseRodRequestStatusProps {
  rodRequest: RodRequest;
  stencilId: CrewMember['id'];
  onClosedRodRequest: (rodRequestId: RodRequest['id']) => void;
  closedRodRequestIds: RodRequest['id'][];
}

export const CloseRodRequestStatus: FC<CloseRodRequestStatusProps> = ({
  rodRequest,
  stencilId,
  onClosedRodRequest,
  closedRodRequestIds,
}) => {
  const { user } = useAppUser();
  const [closeRodRequestError, setCloseRodRequestError] =
    useState<CloseRodRequestByStencilIdError[] | ApolloError | null>(null);

  const [isRodRequestClosedByStencil, setRodRequestClosedByStencil] = useState<boolean>();

  const [closeRodRequestByStencilIdAndRodRequestId, { loading: closeRodRequestLoading }] = useMutation<
    { closeRodRequest: CloseRodRequestByStencilIdPayload },
    { input: CloseRodRequestByStencilIdInput }
  >(CLOSE_ROD_REQUEST_BY_STENCIL_ID, {
    onCompleted: (data) => {
      const errors: CloseRodRequestByStencilIdError[] = get(data, 'closeRodRequestByStencilId.erroredRodRequests', []);
      if (errors && errors.length > 0) {
        setCloseRodRequestError(errors);
        const alreadyClosed = errors.filter(
          (error) =>
            error.rodRequest?.currentStatusI18n === RodRequestStatusCode.CLOSED &&
            error.rodRequest?.id === rodRequest.id
        );
        if (alreadyClosed && alreadyClosed.length > 0) {
          setRodRequestClosedByStencil(!!alreadyClosed.length);
          onClosedRodRequest(rodRequest.id);
        }
      } else {
        setCloseRodRequestError(null);

        const progresslList: RodRequestStencil[] = get(data, 'closeRodRequestByStencilId.rodRequests[0].stencils', []);

        if (progresslList && progresslList.length > 0) {
          const closedByStencil = filter(
            progresslList,
            (progress) => progress.stencil.id == stencilId && progress.status === RodRequestStatusCodeByStencil.CLOSED
          );

          setRodRequestClosedByStencil(!!closedByStencil.length);
          onClosedRodRequest(rodRequest.id);
        }
      }
    },
    onError: (error) => {
      if (error) {
        setCloseRodRequestError(error);
      }
    },
    refetchQueries: ['rodRequests', 'rodRoomStatsSummary'],
  });

  return (
    <>
      <RequestStyled data-testid="close-rod-request-container">
        <Box flexDirection={{ sm: 'column' }} data-testid="rod-request-status">
          <Box
            justifyContent={{ sm: 'space-between' }}
            alignItems={{ sm: 'center' }}
            pl={{ sm: '500' }}
            pb={{ sm: '500' }}
            pr={{ sm: '550' }}
            mt={{ sm: '500' }}
            css={{ background: `${theme.colors.white}` }}
          >
            <Text size="500" weight="600">
              {rodRequest.requestNumber}
            </Text>
            <Box flexDirection={{ sm: 'column' }}>
              <Box alignSelf={{ sm: 'end' }}>
                <Text size="500" weight="600" color="grey.700">
                  (
                  {
                    filter(
                      rodRequest?.caddies,
                      (caddy) => caddy && caddy.currentStatusI18n === RodRequestCaddyStatusCode.RETURNED
                    ).length
                  }
                  /
                  {
                    filter(
                      rodRequest?.caddies,
                      (caddy) =>
                        (caddy && caddy.currentStatusI18n === RodRequestCaddyStatusCode.ISSUED) ||
                        (caddy && caddy.currentStatusI18n === RodRequestCaddyStatusCode.RETURNED)
                    ).length
                  }
                  )
                </Text>
              </Box>
              <Box mt={{ sm: '300' }}>
                <Text size="400" color="grey.700">
                  <Trans>Caddies Returned</Trans>
                </Text>
              </Box>
            </Box>
          </Box>
          <Box
            justifyContent={{ sm: 'space-between' }}
            pt={{ sm: '500' }}
            pb={{ sm: '400' }}
            pr={{ sm: '550' }}
            data-testid="rod-request-weld-status"
          >
            <Box flexDirection={{ sm: 'column' }} pl={{ sm: '500' }} pr={{ sm: '550' }}>
              <Text size="400" color="grey.1000" weight="600">
                <Trans> Welds</Trans>
              </Text>
              <Box pt={{ sm: '100' }}>
                <Text color="grey.600" weight="400" size="300" style={{ opacity: 0.6 }}>
                  0 &nbsp;<Trans>Selected</Trans>
                </Text>
              </Box>
            </Box>
            <Box alignItems={{ sm: 'center' }}>
              <Text size="300" color="grey.600" style={{ opacity: 0.6 }}>
                <Trans>Select</Trans>
              </Text>
              <Box pl={{ sm: '500' }} css={{ cursor: 'pointer' }}>
                <Icon icon="edit" fillColor="bechtel.blue.800" />
              </Box>
            </Box>
          </Box>
        </Box>
        <ProgressWeldFooterStyled data-testid="progressed-weld-footer">
          <FooterButtonStyled
            disabled={
              isRodRequestClosedByStencil ||
              closeRodRequestLoading ||
              findIndex(closedRodRequestIds, (id) => id === rodRequest.id) !== -1
            }
            onClick={() => {
              closeRodRequestByStencilIdAndRodRequestId({
                variables: {
                  input: {
                    rodRequestId: rodRequest.id,
                    closedByUserId: user.id,
                    stencilId: stencilId,
                  },
                },
              });
            }}
          >
            {closeRodRequestLoading && <Spinner />}
            {!closeRodRequestLoading && <Trans> Close</Trans>}
          </FooterButtonStyled>
        </ProgressWeldFooterStyled>
      </RequestStyled>
      {closeRodRequestError && (
        <ErrorBoundary>
          <Suspense fallback={<SuspenseLoader></SuspenseLoader>}>
            <ClosedRodRequestByStencilIdError
              errors={closeRodRequestError}
              close={() => setCloseRodRequestError(null)}
            />
          </Suspense>
        </ErrorBoundary>
      )}
    </>
  );
};
